import React, { useRef, useEffect, useCallback } from "react";
import "./WheelPicker.css";

const ITEM_H = 40;
const WheelPicker = ({
  groups = [],
  currentIndex = 0,
  onIndexChange,
  visibleCount = 7,
}) => {
  const resolvedVisibleCount = Math.max(3, Math.floor(visibleCount));
  const pickerHeight = ITEM_H * resolvedVisibleCount;
  const pickerRef = useRef(null);
  const drumRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartIndex = useRef(null);

  const updateWheel = useCallback(() => {
    if (!drumRef.current) return;
    const offset = pickerHeight / 2 - (currentIndex * ITEM_H + ITEM_H / 2);
    drumRef.current.style.transform = `translateY(${offset}px)`;

    drumRef.current.querySelectorAll(".wp-item").forEach((item, i) => {
      const dist = Math.abs(i - currentIndex);
      item.classList.toggle("active", i === currentIndex);

      const angle = (i - currentIndex) * 18;
      const scaleY = Math.cos((angle * Math.PI) / 180);
      const opacity = Math.max(0, 1 - dist * 0.28);

      item.querySelector(".wp-item-inner").style.transform =
        `perspective(300px) rotateX(${angle}deg) scaleY(${scaleY})`;
      item.style.opacity = opacity;
    });
  }, [currentIndex, pickerHeight]);

  useEffect(() => { updateWheel(); }, [updateWheel]);

  const goTo = useCallback(
    (index) => {
      const clamped = Math.max(0, Math.min(groups.length - 1, index));
      if (clamped !== currentIndex && onIndexChange) {
        onIndexChange(clamped);
      }
    },
    [currentIndex, groups.length, onIndexChange]
  );

  const onMouseDown = (e) => {
    dragStartY.current = e.clientY;
    dragStartIndex.current = currentIndex;
  };
  const onMouseMove = useCallback(
    (e) => {
      if (dragStartY.current === null) return;
      const delta = dragStartY.current - e.clientY;
      goTo(dragStartIndex.current + Math.round(delta / ITEM_H));
    },
    [goTo]
  );
  const onMouseUp = () => { dragStartY.current = null; };

  const onTouchStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    dragStartIndex.current = currentIndex;
  };
  const onTouchMove = useCallback(
    (e) => {
      if (dragStartY.current === null) return;
      const delta = dragStartY.current - e.touches[0].clientY;
      goTo(dragStartIndex.current + Math.round(delta / ITEM_H));
    },
    [goTo]
  );

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      goTo(currentIndex + (e.deltaY > 0 ? 1 : -1));
    },
    [currentIndex, goTo]
  );

  const onListKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        goTo(currentIndex - 1);
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        goTo(currentIndex + 1);
      }

      if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      }

      if (e.key === "End") {
        e.preventDefault();
        goTo(groups.length - 1);
      }
    },
    [currentIndex, goTo, groups.length]
  );

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove]);

  if (!groups || groups.length <= 1) return null;

  return (
    <div
      ref={pickerRef}
      className="wheel-picker"
      style={{ height: pickerHeight }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={() => { dragStartY.current = null; }}
      onWheel={onWheel}
      onKeyDown={onListKeyDown}
      role="listbox"
      aria-label="Collection navigation"
      aria-activedescendant={`wp-item-${currentIndex}`}
      tabIndex={0}
    >
      <div className="wp-selection-band" />
      <div className="wp-drum" ref={drumRef}>
        {groups.map((group, i) => (
          <div
            key={group}
            id={`wp-item-${i}`}
            className="wp-item"
            role="option"
            aria-selected={i === currentIndex}
            onClick={() => {
              goTo(i);
              pickerRef.current?.focus();
            }}
            tabIndex={i === currentIndex ? 0 : -1}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goTo(i); }
              if (e.key === "ArrowUp")   { e.preventDefault(); goTo(currentIndex - 1); }
              if (e.key === "ArrowDown") { e.preventDefault(); goTo(currentIndex + 1); }
            }}
          >
            <div className="wp-item-inner">
              <span className="wp-item-label">{group}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WheelPicker;
